

import { useState } from "react";
import { Store, Package, Trash2, Plus, ChevronDown, ChevronUp } from "lucide-react";
import Modal from "../../components/Modal";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import VendorForm from "../VendorForm";
import ServiceForm from "../ServiceForm";
import PaymentLedger from "../PaymentLedger";
import VendorDetailModal from "../VendorDetailModal";
import { formatCurrency } from "@/lib/data";
import type { Vendor } from "../types";

// Services Modal Content Component
function ServicesModalContent({
  vendorServices,
  onServiceSubmit,
  onDeleteService,
}: {
  vendorServices: any[];
  onServiceSubmit: (data: any) => void;
  onDeleteService?: (serviceId: number) => void;
}) {
  const [showForm, setShowForm] = useState(false);
  // Two-step delete: tap the bin, then confirm on the same row
  const [confirmServiceId, setConfirmServiceId] = useState<number | null>(null);

  return (
    <div className="space-y-4">
      {/* Toggle Form Button */}
      <button
        onClick={() => setShowForm(!showForm)}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-amber-100 text-amber-700 font-semibold text-sm hover:bg-amber-200 transition-colors"
      >
        <Plus className="w-4 h-4" />
        {showForm ? 'Hide Add Service Form' : 'Add New Service'}
        {showForm ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {/* Add Service Form - Toggled */}
      {showForm && (
        <div className="bg-stone-50 rounded-lg p-3 border border-stone-200 animate-in slide-in-from-top-2">
          <h4 className="text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">Add New Service</h4>
          <ServiceForm onSubmit={onServiceSubmit} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {/* Services Table */}
      {vendorServices.length > 0 ? (
        <div className="border border-stone-200 rounded-lg overflow-hidden">
          <div className="bg-stone-50 px-4 py-2 border-b border-stone-200">
            <p className="text-xs font-medium text-stone-600">Services ({vendorServices.length})</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-stone-600 uppercase tracking-wider">Service Name</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-stone-600 uppercase tracking-wider">Cost Price</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-stone-600 uppercase tracking-wider">Selling Price</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-stone-600 uppercase tracking-wider">Commission</th>
                  <th className="text-center px-4 py-2 text-xs font-semibold text-stone-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {vendorServices.map((service) => (
                  <tr key={service.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-stone-800 text-sm">{service.name}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-medium text-stone-700">{formatCurrency(service.base_price)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-medium text-emerald-600">{formatCurrency(service.selling_price)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-medium text-amber-600">{formatCurrency(service.selling_price - service.base_price)}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {confirmServiceId === service.id ? (
                        <span className="inline-flex gap-1">
                          <button
                            type="button"
                            onClick={() => { setConfirmServiceId(null); onDeleteService?.(service.id); }}
                            className="px-2 py-1 rounded-lg bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700"
                          >
                            Delete
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmServiceId(null)}
                            className="px-2 py-1 rounded-lg border border-stone-200 text-stone-600 text-xs hover:bg-stone-50"
                          >
                            Keep
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmServiceId(service.id)}
                          className="p-1.5 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors"
                          title="Delete service"
                          aria-label={`Delete ${service.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 bg-stone-50 rounded-lg border border-stone-200 border-dashed">
          <Package className="w-8 h-8 text-stone-300 mx-auto mb-2" />
          <p className="text-sm text-stone-500">No services added yet</p>
          <p className="text-xs text-stone-400 mt-1">Click "Add New Service" to get started</p>
        </div>
      )}
    </div>
  );
}

interface VendorsModalsProps {
  isModalOpen: boolean;
  isServiceModalOpen: boolean;
  isLedgerModalOpen: boolean;
  isDetailModalOpen: boolean;
  detailVendorId: number | null;
  editingVendor: Vendor | null;
  selectedVendor: Vendor | null;
  vendorServices: any[];
  deleteModalOpen: boolean;
  deletingVendor: Vendor | null;
  deleteLoading: boolean;
  onCloseModal: () => void;
  onCloseServiceModal: () => void;
  onCloseLedgerModal: () => void;
  onCloseDetailModal: () => void;
  onVendorUpdated: () => void;
  onFormSubmit: (data: any) => void;
  onServiceSubmit: (data: any) => void;
  /** Delete a service from the vendor's list (the server refuses services already in use) */
  onDeleteService?: (serviceId: number) => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}

export function VendorsModals({
  isModalOpen,
  isServiceModalOpen,
  isLedgerModalOpen,
  isDetailModalOpen,
  detailVendorId,
  editingVendor,
  selectedVendor,
  vendorServices,
  deleteModalOpen,
  deletingVendor,
  deleteLoading,
  onCloseModal,
  onCloseServiceModal,
  onCloseLedgerModal,
  onCloseDetailModal,
  onVendorUpdated,
  onFormSubmit,
  onServiceSubmit,
  onDeleteService,
  onDeleteConfirm,
  onDeleteCancel,
}: VendorsModalsProps) {
  return (
    <>
      <Modal 
        isOpen={isModalOpen} 
        onClose={onCloseModal} 
        title={editingVendor ? "Edit Vendor" : "Add New Vendor"} 
        size="md"
      >
        <VendorForm
          key={editingVendor?.id ?? "new"}
          initialData={editingVendor ? {
            name: editingVendor.name ?? "",
            vendor_type_id: editingVendor.vendor_type_id ? String(editingVendor.vendor_type_id) : "",
            contact_person: editingVendor.contact_person ?? "",
            phone: editingVendor.phone ?? "",
            email: editingVendor.email ?? "",
            address: editingVendor.address ?? "",
            gst_number: editingVendor.gst_number ?? "",
            commission_type: editingVendor.commission_type ?? "fixed",
            commission_value: editingVendor.commission_value != null ? String(editingVendor.commission_value) : "0",
            status: editingVendor.status ?? "active",
          } : undefined}
          onSubmit={onFormSubmit}
          onCancel={onCloseModal}
        />
      </Modal>

      <Modal 
        isOpen={isServiceModalOpen} 
        onClose={onCloseServiceModal} 
        title={selectedVendor ? `Services - ${selectedVendor.name}` : "Services"} 
        size="lg"
      >
        {selectedVendor && (
          <ServicesModalContent
            vendorServices={vendorServices}
            onServiceSubmit={onServiceSubmit}
            onDeleteService={onDeleteService}
          />
        )}
      </Modal>

      <Modal 
        isOpen={isLedgerModalOpen} 
        onClose={onCloseLedgerModal} 
        title="Payment Ledger" 
        size="lg"
      >
        {selectedVendor && (
          <PaymentLedger 
            vendorId={selectedVendor.id} 
            vendorName={selectedVendor.name} 
            vendorBalance={selectedVendor.balance || 0} 
            onClose={onCloseLedgerModal} 
          />
        )}
      </Modal>

      <DeleteConfirmationModal 
        isOpen={deleteModalOpen} 
        onClose={onDeleteCancel} 
        onConfirm={onDeleteConfirm} 
        title="Delete Vendor" 
        message="Are you sure you want to delete this vendor? This action cannot be undone." 
        itemName={deletingVendor?.name} 
        loading={deleteLoading} 
      />

      <VendorDetailModal 
        isOpen={isDetailModalOpen} 
        vendorId={detailVendorId} 
        onClose={onCloseDetailModal}
        onVendorUpdated={onVendorUpdated}
      />
    </>
  );
}
